import enProducts from '../data/products/en.json';
import zhProducts from '../data/products/zh.json';

const productData = {
  en: enProducts,
  zh: zhProducts
};

/**
 * Get products for a specific language
 * @param {string} language - Language code ('en' or 'zh')
 * @returns {Object} Product data including products array and configuration
 */
export const getProducts = (language = 'en') => {
  return productData[language] || productData.en;
};

/**
 * Get a specific product by ID
 * @param {string} id - Product ID
 * @param {string} language - Language code
 * @returns {Object|null} Product object or null if not found
 */
export const getProductById = (id, language = 'en') => {
  const data = getProducts(language);
  return data.products.find(product => product.id === id) || null;
};

/**
 * Get filtered products based on visibility rules
 * @param {Object} filters - Filter criteria
 * @param {string} language - Language code
 * @returns {Array} Filtered products
 */
export const getFilteredProducts = (filters = {}, language = 'en') => {
  const data = getProducts(language);
  let products = data.products;
  
  // Apply visibility filters first
  products = products.filter(product => {
    // Base visibility check
    if (!product.visibility.enabled) return false;
    
    // Apply custom filters
    if (filters.category && product.category !== filters.category) return false;
    if (filters.type && product.type !== filters.type) return false;
    if (filters.featured && !product.visibility.featured) return false;
    if (filters.new && !product.visibility.new) return false;
    if (filters.onSale && !product.visibility.onSale) return false;
    if (filters.tags && filters.tags.length > 0) {
      const hasTag = filters.tags.some(tag => product.tags.includes(tag));
      if (!hasTag) return false;
    }
    
    // Check seasonal restrictions
    if (product.visibility.seasonal) {
      const now = new Date();
      const start = product.visibility.seasonStart ? new Date(product.visibility.seasonStart) : null;
      const end = product.visibility.seasonEnd ? new Date(product.visibility.seasonEnd) : null;
      
      if (start && now < start) return false;
      if (end && now > end) return false;
    }
    
    return true;
  });
  
  // Apply sorting
  const sortField = filters.sort || data.features.defaultSort || 'priority';
  products.sort((a, b) => {
    switch (sortField) {
      case 'priority':
        return a.visibility.priority - b.visibility.priority;
      case 'new':
        return (b.visibility.new ? 1 : 0) - (a.visibility.new ? 1 : 0);
      case 'featured':
        return (b.visibility.featured ? 1 : 0) - (a.visibility.featured ? 1 : 0);
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return a.visibility.priority - b.visibility.priority;
    }
  });
  
  return products;
};

/**
 * Get products by category
 * @param {string} category - Category ID
 * @param {string} language - Language code
 * @returns {Array} Products in the specified category
 */
export const getProductsByCategory = (category, language = 'en') => {
  return getFilteredProducts({ category }, language);
};

/**
 * Get featured products for homepage
 * @param {number} limit - Maximum number of products to return
 * @param {string} language - Language code
 * @returns {Array} Featured products
 */
export const getFeaturedProducts = (limit = 3, language = 'en') => {
  const products = getFilteredProducts({ featured: true }, language);
  return products.slice(0, limit);
};

/**
 * Get new products
 * @param {number} limit - Maximum number of products to return
 * @param {string} language - Language code
 * @returns {Array} New products
 */
export const getNewProducts = (limit = 3, language = 'en') => {
  const products = getFilteredProducts({ new: true }, language);
  return products.slice(0, limit);
};

/**
 * Get product categories with counts
 * @param {string} language - Language code
 * @returns {Array} Categories with product counts
 */
export const getCategoriesWithCounts = (language = 'en') => {
  const data = getProducts(language);
  const products = data.products.filter(p => p.visibility.enabled && p.visibility.showInCategory);
  
  const categories = {};
  products.forEach(product => {
    if (!categories[product.category]) {
      categories[product.category] = {
        id: product.category,
        count: 0,
        name: product.category // This could be enhanced with a category mapping
      };
    }
    categories[product.category].count++;
  });
  
  return Object.values(categories);
};

/**
 * Check if a feature is enabled globally
 * @param {string} feature - Feature name
 * @param {string} language - Language code
 * @returns {boolean} True if feature is enabled
 */
export const isFeatureEnabled = (feature, language = 'en') => {
  const data = getProducts(language);
  return data.features[feature] || false;
};

/**
 * Get display configuration
 * @param {string} language - Language code
 * @returns {Object} Display configuration
 */
export const getDisplayConfig = (language = 'en') => {
  const data = getProducts(language);
  return data.display || {};
};

/**
 * Get unique tags from all products
 * @param {string} language - Language code
 * @returns {Array} Unique tags
 */
export const getAllTags = (language = 'en') => {
  const data = getProducts(language);
  const products = data.products.filter(p => p.visibility.enabled);
  
  const tags = new Set();
  products.forEach(product => {
    if (product.tags && Array.isArray(product.tags)) {
      product.tags.forEach(tag => tags.add(tag));
    }
  });
  
  return Array.from(tags);
};

/**
 * Get related products for a given product
 * @param {string} productId - Product ID
 * @param {number} limit - Maximum number of related products
 * @param {string} language - Language code
 * @returns {Array} Related products
 */
export const getRelatedProducts = (productId, limit = 3, language = 'en') => {
  const product = getProductById(productId, language);
  if (!product) return [];
  
  const data = getProducts(language);
  const products = data.products.filter(p => 
    p.visibility.enabled && 
    p.visibility.showInRelated && 
    p.id !== productId
  );
  
  // Simple related products logic: same category or shared tags
  const relatedProducts = products.filter(p => 
    p.category === product.category || 
    p.tags.some(tag => product.tags.includes(tag))
  );
  
  // Sort by relevance (more shared tags = higher priority)
  relatedProducts.sort((a, b) => {
    const aSharedTags = a.tags.filter(tag => product.tags.includes(tag)).length;
    const bSharedTags = b.tags.filter(tag => product.tags.includes(tag)).length;
    
    if (aSharedTags !== bSharedTags) {
      return bSharedTags - aSharedTags;
    }
    
    // If same number of shared tags, sort by priority
    return a.visibility.priority - b.visibility.priority;
  });
  
  return relatedProducts.slice(0, limit);
};

/**
 * Check if product should be visible in specific context
 * @param {Object} product - Product object
 * @param {string} context - Visibility context ('catalog', 'homepage', 'category', 'search')
 * @returns {boolean} True if product should be visible
 */
export const isProductVisibleInContext = (product, context = 'catalog') => {
  if (!product.visibility.enabled) return false;
  
  switch (context) {
    case 'catalog':
      return product.visibility.showInCatalog;
    case 'homepage':
      return product.visibility.showOnHomepage;
    case 'category':
      return product.visibility.showInCategory;
    case 'search':
      return product.visibility.showInSearch;
    default:
      return product.visibility.showInCatalog;
  }
};

const productService = {
  getProducts,
  getProductById,
  getFilteredProducts,
  getProductsByCategory,
  getFeaturedProducts,
  getNewProducts,
  getCategoriesWithCounts,
  isFeatureEnabled,
  getDisplayConfig,
  getAllTags,
  getRelatedProducts,
  isProductVisibleInContext
};

export default productService;
